import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { clearSessionToken, getSessionToken } from '@/lib/api';

export function LoginPage({ error, onClearError }) {
  const [hasStaleToken, setHasStaleToken] = useState(false);
  const [cleared, setCleared] = useState(false);

  useEffect(() => {
    // Check if there's a potentially stale token
    const token = getSessionToken();
    if (token) {
      setHasStaleToken(true);
    }
  }, []);

  const handleClearSession = () => {
    clearSessionToken();
    setHasStaleToken(false);
    setCleared(true);
    if (onClearError) onClearError();
  };

  const handleLogin = () => {
    // Clear any previous error and stale tokens before login
    if (onClearError) onClearError();
    clearSessionToken(); // Always clear before new login attempt
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Orq.ai Chat</h1>
          <p className="text-muted-foreground">
            Melden Sie sich an, um fortzufahren
          </p>
        </div>
        
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {hasStaleToken && !cleared && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-700 dark:text-amber-400 mb-3">
              Eine frühere Sitzung wurde erkannt. Falls der Login nicht funktioniert, setzen Sie die Sitzung zurück.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearSession}
              className="w-full"
              data-testid="clear-session-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sitzung zurücksetzen
            </Button>
          </div>
        )}

        {cleared && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-4">
            <p className="text-sm text-green-700 dark:text-green-400">
              ✓ Sitzung zurückgesetzt. Sie können sich jetzt erneut anmelden.
            </p>
          </div>
        )}
        
        <div className="bg-card border rounded-lg p-6 shadow-sm">
          <Button
            onClick={handleLogin}
            className="w-full"
            size="lg"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Mit Google anmelden
          </Button>
        </div>
        
        <p className="text-center text-sm text-muted-foreground mt-6">
          Geschützt durch Emergent Auth
        </p>
      </div>
    </div>
  );
}
